import { Icon } from '@iconify/react';
import { memo, useCallback } from 'react';
import { PlainKeyboardShortcut } from '~/components/chat/commands/KeyboardShortcut';
import { KeyboardShortcutDropdown } from '~/components/chat/commands/KeyboardShortcutDropdown';
import { LegacyIconButton } from '~/components/ui/LegacyIconButton';
import { commandStore } from '~/lib/stores/command';
import type { AnyCommand } from '~/lib/stores/command/types';
import { type CommandContext } from '~/lib/stores/command/types';
import { classNames } from '~/utils/classNames';

interface SearchResultItemProps {
  command: AnyCommand;
  index: number;
  showOptions: boolean;
  commandContext: CommandContext;
  itemRef?: React.RefObject<HTMLDivElement>;
  isSelected?: boolean;
  isFavorited?: boolean;
}

const SearchResultItem = memo((props: SearchResultItemProps) => {
  const { command, commandContext, index, isSelected } = props;
  const icon = command.icon ?? ('category' in command ? command.category?.icon : undefined);

  const handleClick = useCallback(() => {
    if (isSelected) {
      command.action?.(commandContext);
    } else {
      commandStore.setActiveIndex(index);
    }
  }, [isSelected, command, commandContext, index]);

  const handleMouseEnter = useCallback(() => {
    commandStore.setActiveIndex(index, { autoScroll: false });
  }, [index]);

  return (
    <div
      className="pt-10 -mt-10 pb-2 -mb-2 max-w-[calc(var(--radix-popper-anchor-width)-24px)]"
      ref={props.isSelected ? props.itemRef : undefined}
    >
      <div
        className={classNames(
          `flex group/item relative cursor-pointer items-center w-full gap-x-3 px-3 py-2 text-left rounded overflow-hidden h-9`,
          {
            'bg-bolt-elements-item-backgroundAccent text-bolt-elements-item-contentAccent': props.isSelected,
            'hover:bg-bolt-elements-item-backgroundActive bg-transparent': !props.isSelected,
          },
        )}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        role="option"
        aria-selected={props.isSelected}
        aria-label={props.command.name}
      >
        {icon && (
          <div className="size-4">
            <Icon icon={icon.slice(2)} className="shrink-0" aria-hidden="true" />
          </div>
        )}

        <div className="min-w-0 flex flex-col flex-1">
          <div className="text-sm truncate">{props.command.name}</div>
        </div>

        {props.showOptions && (
          <div className="flex items-center gap-1">
            {props.command.shortcuts.some((shortcut) => shortcut.id === 'favorite') && (
              <LegacyIconButton
                className={classNames('group-hover/item:opacity-100 transition-theme', {
                  'opacity-0': !(props.isFavorited || props.isSelected),
                })}
                onClick={(event) => {
                  event.stopPropagation();
                  commandStore.toggleFavorite(props.command.id);
                }}
                title={
                  <div className="flex items-center gap-2">
                    {props.isFavorited ? 'Remove favorite' : 'Add favorite'}{' '}
                    <PlainKeyboardShortcut keys={['Meta', 'Shift', 'L']} />
                  </div>
                }
              >
                <div
                  className={classNames({
                    'i-heroicons:star': !props.isFavorited,
                    'i-heroicons:star-16-solid': props.isFavorited,
                  })}
                />
              </LegacyIconButton>
            )}
            {props.command.shortcuts.length > 1 && (
              <KeyboardShortcutDropdown
                shortcuts={props.command.shortcuts.slice(1)}
                commandContext={props.commandContext}
                uncontrolled
              >
                <LegacyIconButton
                  tabIndex={-1}
                  className={classNames('group-hover/item:opacity-100 transition-theme', {
                    'opacity-0': !props.isSelected,
                  })}
                  title={
                    <div className="flex items-center gap-2">
                      Options
                      <PlainKeyboardShortcut keys={['Meta', 'K']} />
                    </div>
                  }
                >
                  <div className="i-lucide:more-horizontal" />
                </LegacyIconButton>
              </KeyboardShortcutDropdown>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

SearchResultItem.displayName = 'SearchResultItem';

export { SearchResultItem };
